import { Component, OnInit, OnDestroy, ChangeDetectorRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { NgbPaginationModule, NgbTooltipModule, NgbAlertModule, NgbDropdownModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { AuthService } from '../../services/auth.service';
import { AuthUser } from '../../interfaces/auth.interface';
import { StaffHeaderComponent } from '../staff-header/staff-header.component';
import { StaffNavComponent } from '../staff-nav/staff-nav.component';
import { environment } from '../../../environments/environment';
import { UserInformationSummary, getPhoneNumberFrom } from '../../interfaces/user-information-summary.interface';
import { csvFileDateSuffix, downloadCsv } from '../../utils/csv.util';

// Exam List Interface (API Response)
export interface ExamListRecord {
  id: string;
  hoursStudied: string;
  studyDate: string;
  year: number;
  sessionTitle: string;
  notes: string;
  isVerified: boolean;
  enrolledAt: string;
  completedAt: string | null;
  progress: string;
  isCompleted: boolean;
  createdAt: string;
  updatedAt: string;
  userId: string;
  examId: string;
  examListStatusId: number;
  examListPaymentStatusId: number;
  paymentSlip: string | null;
  codeReceipt?: string | null;        // รหัสใบเสร็จ (ว่าง = ยังไม่ออกใบเสร็จ)
  user: {
    id: string;
    email: string;
    cid: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    companyId: string | null;
    userInformation?: UserInformationSummary | null;
  };
  exam: {
    id: string;
    examTitle: string;
    valuerRank: string;
    registrationDeadline: string;
    examDate: string;
    examTime: string;
    examLocation: string;
    examFee: string;
    remarks: string;
    posterFile: string | null;
    certificateFile: string | null;
    createdAt: string;
    updatedAt: string;
    examStatusId: number;
  };
  examListStatus: {
    id: number;
    name: string;
  };
  examListPaymentStatus: {
    id: number;
    name: string;
  };
}

// Filters Interface
export interface ExamFilters {
  searchTerm: string;
  email: string;
  cid: string;
  examTitle: string;
  category: string;
  level: string;
  examListStatus: string;
  paymentStatus: string;
  yearFilter: string;
  dateFrom: string;
  dateTo: string;
}

/** แท็บกรองตามสถานะใบคำขอ พร้อมจำนวนรายการในแต่ละสถานะ */
interface ExamStatusTab {
  /** '' = แท็บรวมทุกสถานะ ค่าอื่นคือชื่อสถานะ */
  value: string;
  name: string;
  /** ชื่อสีของ bootstrap ที่ใช้ทั้งกับแท็บและ badge ในตาราง */
  color: string;
  count: number;
}

@Component({
  selector: 'app-exam-list-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgbPaginationModule,
    NgbTooltipModule,
    NgbAlertModule,
    NgbDropdownModule,
    StaffHeaderComponent,
    StaffNavComponent
  ],
  templateUrl: './exam-list-management.component.html',
  styleUrl: './exam-list-management.component.scss'
})
export class ExamListManagementComponent implements OnInit, OnDestroy {
  currentUser: AuthUser | null = null;
  isMobileMenuOpen = false;
  private userSubscription: Subscription | undefined;

  records: ExamListRecord[] = [];
  filteredRecords: ExamListRecord[] = [];
  isLoading = false;
  isExporting = false;
  error: string | null = null;

  // Filter properties
  filters: ExamFilters = {
    searchTerm: '',
    email: '',
    cid: '',
    examTitle: '',
    category: '',
    level: '',
    examListStatus: '',
    paymentStatus: '',
    yearFilter: '',
    dateFrom: '',
    dateTo: ''
  };

  /** รายการสถานะใบคำขอจาก /exam-list-status */
  examListStatusList: Array<{ id: number; name: string }> = [];

  /**
   * แท็บแยกตามสถานะใบคำขอ สร้างจาก /exam-list-status ที่โหลดมาจริง จึงไม่มี
   * การเพิ่มสถานะใหม่เองในหน้านี้ แท็บแรกคือ "ทั้งหมด" เสมอ
   */
  statusTabs: ExamStatusTab[] = [];

  /** ลำดับแท็บที่ต้องการให้แสดง สถานะนอกรายการนี้ต่อท้ายตามลำดับของ API */
  private readonly statusTabOrder = ['รอ Approve', 'Approved', 'ยกเลิกใบคำขอ', 'คำขอเก่า'];

  /** สีของแต่ละสถานะ ใช้ร่วมกันระหว่างแท็บกับ badge ในตาราง */
  private readonly statusColors: Record<string, string> = {
    'รอ Approve': 'warning',
    'Approved': 'success',
    'ยกเลิกใบคำขอ': 'danger',
    'คำขอเก่า': 'secondary'
  };

  // Pagination
  page = 1;
  pageSize = 10;
  readonly pageSizeOptions = [10, 20, 50, 100];
  collectionSize = 0;

  // Sorting
  sortField = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  private subscription = new Subscription();

  constructor(
    private authService: AuthService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private modalService: NgbModal,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    console.log('🔄 ExamListManagementComponent ngOnInit');

    // Subscribe to auth user
    this.userSubscription = this.authService.currentUser$.subscribe(user => {
      console.log('👤 Current user updated:', user);
      this.currentUser = user;
      this.cdr.detectChanges();
    });

    // Load data immediately
    console.log('📥 Loading exam lists data immediately on init');
    this.loadExamLists();
    this.loadExamListStatuses();
  }

  ngOnDestroy(): void {
    console.log('🗑️ ExamListManagementComponent ngOnDestroy');
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
    this.subscription.unsubscribe();
  }

  onLogout(): void {
    this.authService.logout();
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
  }

  onNavigate(route: string): void {
    this.closeMobileMenu();

    switch (route) {
      case 'users':
        this.router.navigate(['/staff/user-management']);
        break;
      case 'courses':
        this.router.navigate(['/staff/dashboard']);
        break;
      case 'reports':
        this.router.navigate(['/staff/dashboard']);
        break;
      case 'settings':
        this.router.navigate(['/staff/dashboard']);
        break;
      default:
        this.router.navigate(['/staff/dashboard']);
        break;
    }
  }

  /**
   * Load all exam lists records from API
   */
  loadExamLists(): void {
    console.log('🚀 Loading exam lists from API...');
    console.log('📊 Current page:', this.page, 'Page size:', this.pageSize);

    this.isLoading = true;
    this.error = null;

    this.cdr.detectChanges();

    const apiUrl = `${environment.apiUrl}/exam-lists`;
    console.log('📡 Fetching from:', apiUrl);

    this.http.get<ExamListRecord[]>(apiUrl).subscribe({
      next: (response) => {
        console.log('✅ API Response:', response);
        console.log('📦 Data count:', response?.length || 0);
        
        this.records = this.sortByNewestFirst(response || []);
        this.filteredRecords = [...this.records];
        this.recomputeStatusTabs();
        this.collectionSize = this.filteredRecords.length;
        this.isLoading = false;
        
        console.log('✨ Filtered records count:', this.filteredRecords.length);
        console.log('✨ Collection size:', this.collectionSize);
        
        this.cdr.detectChanges();
      },
      error: (error: HttpErrorResponse) => {
        console.error('❌ API Error:', error);
        console.error('❌ Error status:', error.status);
        console.error('❌ Error message:', error.message);
        console.error('❌ Error details:', error.error);
        
        this.error = `เกิดข้อผิดพลาดในการโหลดข้อมูล: ${error.message}`;
        this.records = [];
        this.filteredRecords = [];
        this.collectionSize = 0;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Handle page change
   */
  onPageChange(newPage: number): void {
    console.log('📄 Page changed to:', newPage);
    this.page = newPage;
    this.cdr.detectChanges();
  }

  /**
   * Apply filters to record list (client-side filtering)
   */
  applyFilters(): void {
    console.log('🔍 Applying filters:', this.filters);

    if (!this.records || this.records.length === 0) {
      this.filteredRecords = [];
      this.collectionSize = 0;
      return;
    }

    try {
      this.filteredRecords = this.records.filter(record =>
        this.matchesNonStatusFilters(record) && this.matchesStatusFilter(record)
      );

      this.collectionSize = this.filteredRecords.length;
      this.page = 1; // Reset to first page when filtering
      this.recomputeStatusTabs();
      this.cdr.detectChanges();

    } catch (error) {
      console.error('❌ Error in applyFilters:', error);
      this.filteredRecords = [...this.records];
      this.collectionSize = this.filteredRecords.length;
      this.recomputeStatusTabs();
      this.cdr.detectChanges();
    }
  }

  /**
   * เงื่อนไขกรองทุกข้อ "ยกเว้น" สถานะใบคำขอ แยกออกมาเพื่อให้นับจำนวนในแต่ละแท็บได้
   * โดยยังเคารพตัวกรองอื่นที่ผู้ใช้เลือกไว้
   */
  private matchesNonStatusFilters(record: ExamListRecord): boolean {
    {
        // Search term
        if (this.filters.searchTerm) {
          const searchLower = this.filters.searchTerm.toLowerCase();
          const matchesSearch = 
            record.user.email.toLowerCase().includes(searchLower) ||
            record.user.cid.includes(searchLower) ||
            record.exam.examTitle.toLowerCase().includes(searchLower) ||
            record.sessionTitle.toLowerCase().includes(searchLower) ||
            this.getApplicantName(record).toLowerCase().includes(searchLower) ||
            this.getApplicantOrganization(record).toLowerCase().includes(searchLower);
          
          if (!matchesSearch) return false;
        }

        // Email
        if (this.filters.email && !record.user.email.toLowerCase().includes(this.filters.email.toLowerCase())) {
          return false;
        }

        // CID
        if (this.filters.cid && !record.user.cid.includes(this.filters.cid)) {
          return false;
        }

        // Exam Title
        if (this.filters.examTitle && !record.exam.examTitle.toLowerCase().includes(this.filters.examTitle.toLowerCase())) {
          return false;
        }

        // Valuer Rank (previously category/level)
        if (this.filters.level && record.exam.valuerRank !== this.filters.level) {
          return false;
        }

        // Payment Status
        if (this.filters.paymentStatus && record.examListPaymentStatus.name !== this.filters.paymentStatus) {
          return false;
        }

        // Year Filter
        if (this.filters.yearFilter && record.year.toString() !== this.filters.yearFilter) {
          return false;
        }

        // Date Range
        if (this.filters.dateFrom) {
          const recordDate = new Date(record.studyDate);
          const fromDate = new Date(this.filters.dateFrom);
          if (recordDate < fromDate) return false;
        }

        if (this.filters.dateTo) {
          const recordDate = new Date(record.studyDate);
          const toDate = new Date(this.filters.dateTo);
          if (recordDate > toDate) return false;
        }

        return true;
    }
  }

  // ── แท็บแยกสถานะใบคำขอ ──

  /**
   * โหลดรายการสถานะใบคำขอ เพื่อให้แท็บตามหลังบ้านโดยไม่ต้อง hardcode
   */
  loadExamListStatuses(): void {
    const apiUrl = `${environment.apiUrl}/exam-list-status`;

    const sub = this.http.get<Array<{ id: number; name: string }>>(apiUrl).subscribe({
      next: (response) => {
        this.examListStatusList = response || [];
        this.recomputeStatusTabs();
        this.cdr.detectChanges();
      },
      error: (error: HttpErrorResponse) => {
        console.error('❌ โหลดรายการสถานะใบคำขอสมัครสอบไม่สำเร็จ:', error);
        this.examListStatusList = [];
        this.recomputeStatusTabs();
        this.cdr.detectChanges();
      }
    });

    this.subscription.add(sub);
  }

  /** กรองตามแท็บสถานะที่เลือกอยู่ ('' = ทั้งหมด) */
  private matchesStatusFilter(record: ExamListRecord): boolean {
    if (!this.filters.examListStatus) return true;
    return record.examListStatus?.name === this.filters.examListStatus;
  }

  /**
   * สร้างแท็บสถานะใหม่พร้อมจำนวนในแต่ละแท็บ
   * จำนวนนับจากรายการที่ผ่านตัวกรองอื่นแล้ว ผู้ใช้จึงเห็นล่วงหน้าว่ากดแท็บไหน
   * จะได้กี่รายการ
   */
  private recomputeStatusTabs(): void {
    const matches = (this.records || []).filter(record => this.matchesNonStatusFilters(record));

    this.statusTabs = [
      { value: '', name: 'ทั้งหมด', color: 'dark', count: matches.length },
      ...this.getOrderedStatusList().map(status => ({
        value: status.name,
        name: status.name,
        color: this.getStatusColor(status.name),
        count: matches.filter(record => record.examListStatus?.name === status.name).length
      }))
    ];
  }

  /** เรียงสถานะตาม statusTabOrder ส่วนที่ไม่อยู่ในรายการต่อท้ายตามลำดับของ API */
  private getOrderedStatusList(): Array<{ id: number; name: string }> {
    return [...(this.examListStatusList || [])].sort((a, b) => {
      const ai = this.statusTabOrder.indexOf(a?.name);
      const bi = this.statusTabOrder.indexOf(b?.name);
      if (ai === -1 && bi === -1) return 0;
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
  }

  /** เลือกแท็บสถานะ */
  selectStatusTab(tab: ExamStatusTab): void {
    if (this.filters.examListStatus === tab.value) return;

    this.filters.examListStatus = tab.value;
    this.applyFilters();
  }

  isActiveStatusTab(tab: ExamStatusTab): boolean {
    return (this.filters.examListStatus || '') === tab.value;
  }

  private getStatusColor(statusName?: string): string {
    return this.statusColors[statusName || ''] || 'secondary';
  }

  /**
   * Get current page records
   */
  getCurrentPageRecords(): ExamListRecord[] {
    const startIndex = (this.page - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    return this.filteredRecords.slice(startIndex, endIndex);
  }

  /**
   * Clear all filters
   */
  clearFilters(): void {
    this.filters = {
      searchTerm: '',
      email: '',
      cid: '',
      examTitle: '',
      category: '',
      level: '',
      examListStatus: '',
      paymentStatus: '',
      yearFilter: '',
      dateFrom: '',
      dateTo: ''
    };
    this.page = 1;
    this.applyFilters();
  }

  /**
   * Sort records by field
   */
  /**
   * กดหัวคอลัมน์เพื่อเรียงลำดับ
   * ครั้งแรก = น้อยไปมาก (asc), ครั้งที่สอง = มากไปน้อย (desc), ครั้งที่สาม = ยกเลิกการเรียง
   */
  sortBy(field: string): void {
    if (this.sortField !== field) {
      this.sortField = field;
      this.sortDirection = 'asc';
    } else if (this.sortDirection === 'asc') {
      this.sortDirection = 'desc';
    } else {
      // ยกเลิกการเรียง → กลับไปเรียงรายการล่าสุดอยู่บนสุด
      this.sortField = '';
      this.sortDirection = 'asc';
      this.filteredRecords = this.sortByNewestFirst(this.filteredRecords);
      this.page = 1;
      this.cdr.detectChanges();
      return;
    }

    this.page = 1;

    this.filteredRecords.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (field) {
        case 'email':
          aValue = a.user.email;
          bValue = b.user.email;
          break;
        case 'cid':
          aValue = a.user.cid;
          bValue = b.user.cid;
          break;
        case 'applicantName':
          aValue = this.getApplicantName(a);
          bValue = this.getApplicantName(b);
          break;
        case 'createdAt':
          aValue = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          bValue = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          break;
        case 'vatId':
          aValue = this.getVatId(a);
          bValue = this.getVatId(b);
          break;
        case 'valuerId':
          aValue = this.getValuerId(a);
          bValue = this.getValuerId(b);
          break;
        case 'valuerRank':
          aValue = a.exam?.valuerRank || '';
          bValue = b.exam?.valuerRank || '';
          break;
        case 'price':
          aValue = Number(a.exam?.examFee ?? 0) || 0;
          bValue = Number(b.exam?.examFee ?? 0) || 0;
          break;
        case 'paymentSlip':
          aValue = this.hasPaymentSlip(a) ? 1 : 0;
          bValue = this.hasPaymentSlip(b) ? 1 : 0;
          break;
        case 'paymentStatus':
          aValue = a.examListPaymentStatus?.name || '';
          bValue = b.examListPaymentStatus?.name || '';
          break;
        case 'reviewStatus':
          aValue = a.examListStatus?.name || '';
          bValue = b.examListStatus?.name || '';
          break;
        case 'receiptStatus':
          aValue = a.codeReceipt ? 1 : 0;
          bValue = b.codeReceipt ? 1 : 0;
          break;
        case 'organization':
          aValue = this.getApplicantOrganization(a);
          bValue = this.getApplicantOrganization(b);
          break;
        case 'examTitle':
          aValue = a.exam.examTitle;
          bValue = b.exam.examTitle;
          break;
        case 'studyDate':
          aValue = new Date(a.studyDate);
          bValue = new Date(b.studyDate);
          break;
        case 'hoursStudied':
          aValue = parseFloat(a.hoursStudied);
          bValue = parseFloat(b.hoursStudied);
          break;
        case 'year':
          aValue = a.year;
          bValue = b.year;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return this.sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return this.sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }

  /**
   * Get sort icon for column
   */
  getSortIcon(field: string): string {
    if (this.sortField !== field) return '⇅';
    return this.sortDirection === 'asc' ? '▲' : '▼';
  }

  /**
   * Change page size
   */
  changePageSize(size: number): void {
    this.pageSize = size;
    this.page = 1;
    this.cdr.detectChanges();
  }

  /**
   * Refresh data
   */
  refreshData(): void {
    console.log('🔄 Refreshing data...');
    this.loadExamLists();
  }

  /**
   * Export to CSV
   */
  exportToCSV(): void {
    if (this.isExporting) {
      return;
    }

    console.log('📊 Exporting to CSV...');

    // หน้านี้โหลดข้อมูลทั้งหมดมาแล้ว (กรอง/แบ่งหน้าฝั่ง client)
    // จึง Export จากรายการที่ผ่านตัวกรองได้ทันที
    const rows = this.filteredRecords || [];

    if (rows.length === 0) {
      alert('ไม่พบข้อมูลที่ตรงกับเงื่อนไขการกรอง');
      return;
    }

    this.isExporting = true;
    this.cdr.detectChanges();

    // เรียงตามหัวตาราง แล้วต่อท้ายด้วยข้อมูลที่ไม่ได้แสดงบนตาราง
    const headers = [
      'วันและเวลาที่ยื่นคำขอ',
      'อีเมล',
      'ชื่อ-นามสกุล',
      'สถานะสมาชิก (VAT ID)',
      'สถานะผู้ประเมินหลัก (Valuer ID)',
      'สอบชั้น',
      'ประจำปี',
      'สถานะการตรวจสอบเอกสาร',
      'ราคา (บาท)',
      'หลักฐานการโอน',
      'สถานะการชำระเงิน',
      'สถานะการออกใบเสร็จ',
      'หมายเหตุ',
      // ข้อมูลเพิ่มเติม (ไม่ได้แสดงบนตาราง)
      'เลขบัตรประชาชน',
      'เบอร์โทร',
      'หน่วยงาน',
      'ชื่อการสอบ',
      'หัวข้อ/รอบสอบ',
      'วันที่สอบ',
      'เวลาสอบ',
      'สถานที่สอบ'
    ];

    const csvRows = rows.map(record => [
      this.formatDateTime(record.createdAt),
      record.user?.email || '-',
      this.getApplicantName(record),
      this.getVatId(record),
      this.getValuerId(record),
      record.exam?.valuerRank || '-',
      record.year,
      record.examListStatus?.name || '-',
      Number(record.exam?.examFee || 0).toFixed(2),
      this.hasPaymentSlip(record) ? 'มีหลักฐาน' : 'ไม่มีหลักฐาน',
      record.examListPaymentStatus?.name || '-',
      this.getReceiptStatusText(record),
      (record.notes || '-').replace(/[\r\n]+/g, ' '),
      record.user?.cid || '-',
      getPhoneNumberFrom(record.user?.userInformation),
      this.getApplicantOrganization(record),
      record.exam?.examTitle || '-',
      record.sessionTitle || '-',
      this.formatDate(record.exam?.examDate || ''),
      record.exam?.examTime || '-',
      record.exam?.examLocation || '-'
    ]);

    downloadCsv(`exam-list_${csvFileDateSuffix()}`, headers, csvRows);
    console.log(`✅ Exported ${csvRows.length} rows to CSV`);

    this.isExporting = false;
    this.cdr.detectChanges();
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('th-TH');
  }

  /** วันและเวลาที่ผู้ใช้ยื่นคำขอเข้ามาในระบบ */
  formatDateTime(dateString?: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return `${date.toLocaleDateString('th-TH')} ${date.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })} น.`;
  }

  /** เลขที่สมาชิก (VAT ID) */
  getVatId(record: ExamListRecord): string {
    return record?.user?.userInformation?.vatId || '-';
  }

  /** เลขที่ผู้ประเมินหลัก (Valuer ID) */
  getValuerId(record: ExamListRecord): string {
    return record?.user?.userInformation?.valuerId || '-';
  }

  /** มีหลักฐานการโอนหรือยัง */
  hasPaymentSlip(record: ExamListRecord): boolean {
    return !!record?.paymentSlip && String(record.paymentSlip).trim() !== '';
  }

  openPaymentSlip(record: ExamListRecord, event: Event): void {
    event.stopPropagation();
    if (this.hasPaymentSlip(record)) {
      window.open(record.paymentSlip as string, '_blank');
    }
  }

  /** สถานะการออกใบเสร็จ */
  getReceiptStatusText(record: ExamListRecord): string {
    return record?.codeReceipt ? 'ออกใบเสร็จแล้ว' : 'รอดำเนินการ';
  }

  getReceiptStatusClass(record: ExamListRecord): string {
    return record?.codeReceipt ? 'bg-success' : 'bg-secondary';
  }

  isSortedBy(field: string): boolean {
    return this.sortField === field;
  }

  /**
   * ชื่อ-นามสกุลผู้สมัครสอบ
   */
  getApplicantName(record: ExamListRecord): string {
    const info = record?.user?.userInformation;
    const first = info?.firstName || info?.firstNameThai || info?.firstNameEnglish || '';
    const last = info?.lastName || info?.lastNameThai || info?.lastNameEnglish || '';
    return `${first} ${last}`.trim() || '-';
  }

  /**
   * หน่วยงาน/บริษัทของผู้สมัครสอบ
   */
  getApplicantOrganization(record: ExamListRecord): string {
    const info = record?.user?.userInformation;
    return info?.company?.organizationName || info?.workplaceName || info?.organization || '-';
  }

  /**
   * เรียงรายการจากใหม่ไปเก่า (รายการล่าสุดอยู่บนสุด)
   */
  private sortByNewestFirst(items: ExamListRecord[]): ExamListRecord[] {
    return [...(items || [])].sort((a, b) => {
      const timeA = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
      const timeB = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      return timeB - timeA;
    });
  }

  /**
   * Get level badge class
   */
  getLevelBadgeClass(level: string): string {
    switch (level) {
      case 'beginner':
        return 'bg-info';
      case 'intermediate':
        return 'bg-primary';
      case 'advanced':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  }

  /**
   * Get level text
   */
  getLevelText(level: string): string {
    switch (level) {
      case 'beginner':
        return 'เริ่มต้น';
      case 'intermediate':
        return 'กลาง';
      case 'advanced':
        return 'สูง';
      default:
        return level;
    }
  }

  /**
   * Get exam list status badge class
   */
  getExamListStatusBadgeClass(record: ExamListRecord): string {
    const statusName = record.examListStatus?.name;

    switch (statusName) {
      case 'อนุมัติ':
      case 'ผ่านการอนุมัติ':
        return 'bg-success';
      case 'รอการอนุมัติ':
        return 'bg-warning';
      case 'ไม่อนุมัติ':
        return 'bg-danger';
      default:
        // สถานะมาตรฐานใช้สีชุดเดียวกับแท็บ
        return `bg-${this.getStatusColor(statusName)}`;
    }
  }

  /**
   * Get exam payment status badge class
   */
  getExamPaymentStatusBadgeClass(record: ExamListRecord): string {
    const statusName = record.examListPaymentStatus.name;
    
    switch (statusName) {
      case 'ชำระเงินเสร็จสิ้น':
      case 'ชำระแล้ว':
        return 'bg-success';
      case 'รอชำระเงิน':
      case 'ยังไม่ได้ชำระเงิน':
        return 'bg-warning';
      case 'รอตรวจสอบยอดชำระ':
      case 'รอตรวจสอบ':
        return 'bg-info';
      case 'ยกเลิก':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  }

  /**
   * Get verification badge class
   */
  getVerificationBadgeClass(isVerified: boolean): string {
    return isVerified ? 'bg-success' : 'bg-warning';
  }

  /**
   * Get verification text
   */
  getVerificationText(isVerified: boolean): string {
    return isVerified ? 'ยืนยันแล้ว' : 'รอยืนยัน';
  }

  /**
   * View record details
   */
  viewRecord(record: ExamListRecord): void {
    console.log('👁️ View record:', record);
    // Navigate to exam detail page
    this.router.navigate(['/staff/exam-list-management/detail', record.id]);
  }

  /**
   * Edit record
   */
  editRecord(record: ExamListRecord): void {
    console.log('🔧 Edit record:', record);
    // TODO: Implement edit record modal
    alert(`แก้ไขการสอบ: ${record.id}`);
  }

  /**
   * Delete record
   */
  deleteRecord(record: ExamListRecord): void {
    console.log('🗑️ Delete record:', record);
    const confirmation = confirm(`คุณต้องการลบใบคำขอสมัครสอบ ${record.exam.examTitle} หรือไม่?\n\nการดำเนินการนี้ไม่สามารถยกเลิกได้!`);
    
    if (confirmation) {
      const apiUrl = `${environment.apiUrl}/exam-lists/${record.id}`;
      console.log('📡 Sending DELETE request to:', apiUrl);

      this.http.delete(apiUrl).subscribe({
        next: (response) => {
          console.log('✅ API Response:', response);
          const index = this.records.findIndex(r => r.id === record.id);
          if (index > -1) {
            this.records.splice(index, 1);
            this.applyFilters();
          }
          alert(`✅ ลบใบคำขอสมัครสอบเรียบร้อยแล้ว`);
        },
        error: (error: HttpErrorResponse) => {
          console.error('❌ API Error:', error);
          alert(`เกิดข้อผิดพลาดในการลบ: ${error.message}`);
        }
      });
    }
  }

  /**
   * Verify record
   */
  verifyRecord(record: ExamListRecord): void {
    console.log('✅ Verify record:', record);
    const confirmation = confirm(`คุณต้องการยืนยันใบคำขอสมัครสอบสำหรับ ${record.exam.examTitle} หรือไม่?`);
    
    if (confirmation) {
      const apiUrl = `${environment.apiUrl}/exam-lists/${record.id}`;
      console.log('📡 Sending PATCH request to:', apiUrl);

      this.http.patch(apiUrl, { isVerified: true }).subscribe({
        next: (response) => {
          console.log('✅ API Response:', response);
          record.isVerified = true;
          this.cdr.detectChanges();
          alert(`✅ ยืนยันใบคำขอสมัครสอบเรียบร้อยแล้ว`);
        },
        error: (error: HttpErrorResponse) => {
          console.error('❌ API Error:', error);
          alert(`เกิดข้อผิดพลาดในการยืนยัน: ${error.message}`);
        }
      });
    }
  }

  /**
   * Unverify record
   */
  unverifyRecord(record: ExamListRecord): void {
    console.log('❌ Unverify record:', record);
    const confirmation = confirm(`คุณต้องการยกเลิกการยืนยันใบคำขอสมัครสอบสำหรับ ${record.exam.examTitle} หรือไม่?`);
    
    if (confirmation) {
      const apiUrl = `${environment.apiUrl}/exam-lists/${record.id}`;
      console.log('📡 Sending PATCH request to:', apiUrl);

      this.http.patch(apiUrl, { isVerified: false }).subscribe({
        next: (response) => {
          console.log('✅ API Response:', response);
          record.isVerified = false;
          this.cdr.detectChanges();
          alert(`❌ ยกเลิกการยืนยันใบคำขอสมัครสอบเรียบร้อยแล้ว`);
        },
        error: (error: HttpErrorResponse) => {
          console.error('❌ API Error:', error);
          alert(`เกิดข้อผิดพลาดในการยกเลิกการยืนยัน: ${error.message}`);
        }
      });
    }
  }

  /**
   * Track by function for ngFor optimization
   */
  trackByRecordId(index: number, record: ExamListRecord): string {
    return record.id;
  }

  /**
   * Math helper for template
   */
  min(a: number, b: number): number {
    return Math.min(a, b);
  }

  /**
   * Handle dropdown open/close state
   */
  onDropdownOpenChange(isOpen: boolean, recordId: string): void {
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        const dropdownButton = document.getElementById(`dropdownMenuButton-${recordId}`);
        const tableRow = dropdownButton?.closest('tr');
        const tableContainer = dropdownButton?.closest('.table-responsive');

        if (tableRow) {
          if (isOpen) {
            tableRow.classList.add('dropdown-active');
            tableRow.style.position = 'relative';
            tableRow.style.zIndex = '1050';

            if (tableContainer) {
              (tableContainer as HTMLElement).style.overflow = 'visible';
            }

            const rowRect = tableRow.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const dropdownMenu = tableRow.querySelector('.dropdown-menu') as HTMLElement;

            if (dropdownMenu && rowRect.bottom > viewportHeight - 200) {
              dropdownMenu.style.top = 'auto';
              dropdownMenu.style.bottom = '100%';
              dropdownMenu.style.marginBottom = '2px';
              dropdownMenu.style.marginTop = '0';
            }
          } else {
            tableRow.classList.remove('dropdown-active');
            tableRow.style.position = '';
            tableRow.style.zIndex = '';

            if (tableContainer && window.innerWidth >= 992) {
              (tableContainer as HTMLElement).style.overflow = 'visible';
            } else if (tableContainer) {
              (tableContainer as HTMLElement).style.overflow = '';
            }

            const dropdownMenu = tableRow.querySelector('.dropdown-menu') as HTMLElement;
            if (dropdownMenu) {
              dropdownMenu.style.top = '';
              dropdownMenu.style.bottom = '';
              dropdownMenu.style.marginBottom = '';
              dropdownMenu.style.marginTop = '';
            }
          }
        }
      }, 0);
    });
  }
}
